/**
 * v2Lint.js — Canvas V2 semantic lint engine (task #166).
 *
 * Pure-function lint pass over a flat `{nodes, connections}` canvas payload.
 * Produces structured diagnostics keyed by source nodeId so renderers can
 * stamp a badge on the offending node + surface a tooltip.
 *
 * WHY THIS EXISTS
 * ───────────────
 * Conductor OSS's def-time UX has a single state: "saved / not saved". The moment
 * you press Save, the workflow either parses (green) or rejects with a
 * stack trace (red). There is no live feedback about SEMANTIC correctness
 * — e.g., a SWITCH with no default case is accepted at save-time, then
 * fails at runtime when the first off-path input arrives.
 *
 * Canvas V2 fixes this: the lint pass runs on every render and node chrome
 * paints a small diagnostic dot (error = rose, warn = amber, info = sky)
 * the moment the customer types something semantically wrong. We expect
 * this to catch ~90% of "my workflow didn't start" support tickets at
 * def-time instead of at runtime.
 *
 * DESIGN
 * ──────
 *   lintCanvas(payload) → { diagnostics: Diagnostic[] }
 *
 *   Diagnostic = {
 *     nodeId:   string,       // source flat-canvas node id (for UI anchoring)
 *     severity: 'error' | 'warn' | 'info',
 *     rule:     string,       // stable id — test assertions pin against this
 *     message:  string,       // human-friendly; used as the tooltip body
 *   }
 *
 * TWO PASSES
 * ──────────
 * 1. **Raw-canvas pass** (always runs). Rules operate on the flat-canvas
 *    node + config directly. This catches the common "customer typed
 *    something broken" state — e.g., an empty switch expression — even
 *    when flatToIR refuses to build an IR for the workflow.
 *
 * 2. **IR-graph pass** (runs when flatToIR succeeds). Catches rules that
 *    need the hierarchical view — unreachable-node detection, fork-join
 *    semantics where the IR collapses the JOIN into the parent.
 *
 * If flatToIR fails, pass 2 is skipped and the failure is surfaced as a
 * single `lint/parse-failed` diagnostic against `__payload__`. The lint
 * engine never crashes the canvas.
 */

import { flatToIR } from '../ir/flatToIR';
import { IR_KIND, normalizeCanvasType, CANVAS_TYPE } from '../ir/kinds';
import {
  getContainerHint,
  getContainerChildIds,
  CONTAINER_HINT_KIND,
} from '../ir/containerHint';
// RC-116a — NODE_TYPES is the schema source of truth used by the
// drift validator at the bottom of this file. The validator asserts
// that every field this lint reads (LINT_FIELD_REFERENCES) is
// actually declared in NODE_TYPES[type].configSchema, catching the
// RC-114 / RC-112d class of bug at module init.
import { NODE_TYPES } from '../../data/soarNodes';
import { branchIdentifiers } from '../canvas/branchIdentity';

// ───────────────────────────────────────────────────────────────────
//  Public API
// ───────────────────────────────────────────────────────────────────

export const SEVERITY = Object.freeze({
  ERROR: 'error',
  WARN:  'warn',
  INFO:  'info',
});

/**
 * Sentinel diagnostic that applies to the whole payload rather than a
 * specific node. Renderers can surface these as a canvas-level banner.
 */
export const PAYLOAD_NODE_ID = '__payload__';

/**
 * Lint a flat canvas payload. Safe to call on any input — never throws.
 *
 * @param {{ nodes: object[], connections: object[] } | null | undefined} payload
 * @returns {{ diagnostics: Array<{
 *   nodeId: string, severity: string, rule: string, message: string
 * }> }}
 */
export function lintCanvas(payload) {
  const diagnostics = [];

  if (!payload || !Array.isArray(payload.nodes) || !Array.isArray(payload.connections)) {
    diagnostics.push({
      nodeId: PAYLOAD_NODE_ID,
      severity: SEVERITY.ERROR,
      rule: 'lint/malformed-payload',
      message: 'Payload is not a valid { nodes, connections } shape.',
    });
    return { diagnostics };
  }

  const push = (d) => diagnostics.push(d);

  // ── Pass 1: raw-canvas rules ─────────────────────────────────
  // Run regardless of whether flatToIR succeeds. These rules read the
  // customer's literal config surface and catch the "you typed something
  // wrong" state that would block IR construction.
  runRawCanvasRules(payload, push);

  // ── Pass 2: IR-graph rules ───────────────────────────────────
  // Only runs if flatToIR succeeds. Handles rules that need the
  // hierarchical view (reachability, fork-join semantics).
  let ir = null;
  try {
    ir = flatToIR(normalize(payload));
  } catch (err) {
    diagnostics.push({
      nodeId: PAYLOAD_NODE_ID,
      severity: SEVERITY.ERROR,
      rule: 'lint/parse-failed',
      message: `Workflow cannot be parsed: ${err && err.message ? err.message : String(err)}`,
    });
    return { diagnostics };
  }

  const ctx = { payload, ir, push };
  runIrRules(ctx);

  return { diagnostics };
}

/**
 * Bucket diagnostics by source node id. Convenient for UI — one call lets
 * the renderer stamp `data.diagnostics: Diagnostic[]` on every rfNode.
 *
 * @param {Array} diagnostics
 * @returns {Map<string, Array>}
 */
export function diagnosticsByNodeId(diagnostics) {
  const byId = new Map();
  for (const d of diagnostics || []) {
    const list = byId.get(d.nodeId);
    if (list) list.push(d);
    else byId.set(d.nodeId, [d]);
  }
  return byId;
}

// ───────────────────────────────────────────────────────────────────
//  Pass 1: raw-canvas rules
// ───────────────────────────────────────────────────────────────────

function runRawCanvasRules(payload, push) {
  const outsByNode = indexOutgoing(payload.connections);

  for (const node of payload.nodes) {
    if (!node || !node.id) continue;
    const cType = normalizeCanvasType(node.type);
    const cfg = node.config || {};
    const outs = outsByNode.get(node.id) || [];

    try {
      if (cType === CANVAS_TYPE.CONDITION) {
        lintRawSwitch(node, cfg, outs, push);
      } else if (cType === CANVAS_TYPE.PARALLEL_FORK) {
        lintRawFork(node, cfg, outs, push);
      } else if (cType === CANVAS_TYPE.LOOP) {
        lintRawLoop(node, cfg, outs, push);
      } else if (cType === CANVAS_TYPE.SUB_WORKFLOW) {
        lintRawSubWorkflow(node, cfg, push);
      } else if (cType === CANVAS_TYPE.TERMINATE) {
        lintRawTerminate(node, cfg, push);
      }
    } catch { /* a broken rule must never break the lint pass */ }
  }
}

function lintRawSwitch(node, cfg, outs, push) {
  // switch/empty-expression
  // Accept the canonical `expression` field plus the historical aliases
  // `switchExpression` (Canvas-AI generator) / `conditionExpression` /
  // `condition` (legacy importer). The IR builder reads the same set of
  // fallbacks so the lint can't cry foul on something the IR happily
  // resolved.
  //
  // Also accept `cfg.action` — GEL-materialized switches like the
  // Enforcement Decision node use `action: "enforcement_decision"` instead
  // of a literal expression because the runtime knows how to evaluate that
  // action directly (DPG result + policy result → ALLOW/BLOCK). The
  // GEL materializer wires their cases the same way as a regular switch,
  // so they're functionally equivalent — but they don't have a textual
  // expression for a human to read. Same exemption applies to any future
  // platform-driven switch that delegates evaluation to a named action.
  //
  // G428 — Also accept the V2 declarative-conditions schema where each
  // case carries its own expression on `cfg.conditions[*].expression`
  // (Canvas-AI templates such as Suspicious Login Response use this
  // shape exclusively). Any non-empty per-condition expression means
  // the switch has SOMETHING to evaluate — the per-case body is the
  // wire-level expression and the case label is the routing key, so
  // the legacy "single expression" check doesn't apply.
  const declarativeConditions = Array.isArray(cfg.conditions) ? cfg.conditions : null;
  const hasDeclarativeExpr = declarativeConditions
    && declarativeConditions.some(
      (cond) => cond && typeof cond.expression === 'string' && cond.expression.trim()
    );
  const candidate =
    (typeof cfg.expression === 'string' && cfg.expression)
    || (typeof cfg.switchExpression === 'string' && cfg.switchExpression)
    || (typeof cfg.conditionExpression === 'string' && cfg.conditionExpression)
    || (typeof cfg.condition === 'string' && cfg.condition)
    || (typeof cfg.action === 'string' && cfg.action)
    || '';
  const expr = candidate.trim();
  if (!expr && !hasDeclarativeExpr) {
    push({
      nodeId: node.id,
      severity: SEVERITY.ERROR,
      rule: 'switch/empty-expression',
      message: 'Switch expression is empty — nothing to evaluate against.',
    });
  }

  // switch/no-cases + switch/missing-default
  //
  // G426 — Consult the typed `_containerChildren` hint (see
  // utils/ir/containerHint.js). The V2 canvas + backend materializer now
  // express case bodies via `casePorts: { "<caseName>": [childCanvasId, ...] }`
  // on the switch node itself, not always via per-case `sourceHandle`
  // edges out of the switch. When the hint is present we treat its
  // case-port names as the authoritative case set; flat outgoing handles
  // are still merged in so legacy / hint-less workflows keep working.
  // Without this, every hint-emitted switch fired a false-positive
  // `switch/no-cases` ERR badge that hung around forever.
  const handles = new Set();
  for (const c of outs) {
    const h = typeof c.sourceHandle === 'string' && c.sourceHandle.length > 0
      ? c.sourceHandle
      : 'default';
    handles.add(h);
  }
  const hint = getContainerHint(node);
  if (hint && hint.kind === CONTAINER_HINT_KIND.SWITCH
      && hint.casePorts && typeof hint.casePorts === 'object') {
    for (const port of Object.keys(hint.casePorts)) {
      // Only count a port as "wired" if it actually has children inside it;
      // an empty casePorts entry is the same as no case at all.
      const list = hint.casePorts[port];
      if (Array.isArray(list) && list.length > 0) handles.add(port);
    }
  }
  // G428 — Declarative-conditions schema: each entry in `cfg.conditions`
  // is a case (its `label` / `outputVariable` is the route key) and
  // `cfg.defaultOutput` (string) is the fallthrough arm. The runtime
  // routes on these without needing outgoing per-case sourceHandle
  // edges on the switch node itself, so the lint must treat them as
  // wired cases. Without this, every Canvas-AI Switch fired a
  // false-positive `switch/no-cases` ERR even though the workflow
  // executed fine.
  if (declarativeConditions) {
    for (const cond of declarativeConditions) {
      if (!cond) continue;
      const port = (typeof cond.outputVariable === 'string' && cond.outputVariable)
        || (typeof cond.label === 'string' && cond.label)
        || (typeof cond.id === 'string' && cond.id);
      if (port) handles.add(port);
    }
  }
  if (typeof cfg.defaultOutput === 'string' && cfg.defaultOutput.trim()) {
    handles.add('default');
  }
  if (handles.size === 0) {
    push({
      nodeId: node.id,
      severity: SEVERITY.ERROR,
      rule: 'switch/no-cases',
      message: 'Switch has no outgoing edges. Add at least one named case or a default.',
    });
  } else if (!handles.has('default')) {
    // Boolean conditions exhaustively cover the {true, false} space —
    // a switch wired only with `true` and `false` ports doesn't need a
    // default arm because there's no third value the runtime could
    // legally produce. Suppressing the warn here keeps the canvas
    // visually clean for the common Conductor binary-validity SWITCH
    // pattern (validity_switch, has_permission, etc.).
    const portNames = new Set([...handles].map((h) => String(h).toLowerCase()));
    portNames.delete('default');
    const isBooleanOnly = portNames.size > 0
      && [...portNames].every((p) => p === 'true' || p === 'false');
    if (!isBooleanOnly) {
      push({
        nodeId: node.id,
        severity: SEVERITY.WARN,
        rule: 'switch/missing-default',
        message:
          'Switch has no default case. Inputs that do not match any named case '
          + 'will error at runtime. Add a default arm to make the fallthrough '
          + 'intentional.',
      });
    }
  }
}

function lintRawFork(node, cfg, outs, push) {
  const isDynamic = !!cfg.isDynamic;

  // RC-114 — Schema-aware branch counting.
  //
  // Pre-RC-114 this linter ONLY counted edges whose sourceHandle started
  // with the literal `branch_` prefix. That convention works for legacy
  // canvases where ports are auto-named branch_1/branch_2/etc. But the
  // AI Generator (and operators authoring fresh forks) routinely use
  // SEMANTIC port names that match the schema's config.branches[].name
  // values — e.g. `secret_branch`, `asset_branch`, `email_branch`.
  // None of those start with `branch_`, so the linter saw 0 branches
  // and fired `fork/degenerate-leg-count` ERR even on perfectly
  // connected forks. Sony hit this immediately after the AI auto-fix
  // wired the connections correctly: workflow validated globally
  // ("Valid" at bottom-right) but the per-node ERR badge stuck red.
  //
  // Fix: build a set of declared branch names from cfg.branches[]
  // (schema source of truth) and count any sourceHandle that matches
  // either the legacy prefix OR a declared name. Both back-compat and
  // schema-aware.
  //
  // RC-112e — this restated the identity rule, and the restatement took only
  // the FIRST field a branch happened to carry (`name || id || label ||
  // value`). A branch declared `{ name: 'approve', id: 'branch_1' }` and
  // wired on `branch_1` therefore counted as no leg at all. Branch identity
  // now has one owner — utils/canvas/branchIdentity.js — shared with
  // v3Validate and v4Validate. It accepts every field, so the leg count can
  // only rise, and this rule can only fire less often than it did.
  const declaredBranchNames = new Set();
  if (Array.isArray(cfg.branches)) {
    for (const b of cfg.branches) {
      for (const id of branchIdentifiers(b)) declaredBranchNames.add(id);
    }
  }

  // Count "real" branches: outgoing edges whose sourceHandle is either
  // the legacy `branch_*` form OR a name declared in config.branches.
  // 'default' is excluded for dyn-fork pass-through. This makes the
  // linter consistent with the dangling-branch check in v3Validate.js
  // (RC-112d aligned that to read cfg.branches too).
  const branchLegs = outs.filter((c) => {
    const h = typeof c.sourceHandle === 'string' ? c.sourceHandle : '';
    if (!h || h === 'default') return false;
    return h.startsWith('branch_') || declaredBranchNames.has(h);
  });

  // G426 — Same fix as lintRawSwitch: the container-hint `branches` /
  // `resolvedBranches` arrays are the authoritative source for leg count
  // in V2 canvases. Without consulting them, every fork with hint-only
  // children fired a false-positive `fork/degenerate-leg-count` ERR.
  // We count a branch as "real" only if it has at least one childId
  // (an empty branch is the same as no branch at all).
  const hint = getContainerHint(node);
  let hintBranchCount = 0;
  if (hint) {
    if (hint.kind === CONTAINER_HINT_KIND.FORK && Array.isArray(hint.branches)) {
      hintBranchCount = hint.branches.filter(
        (b) => b && Array.isArray(b.childIds) && b.childIds.length > 0
      ).length;
    } else if (hint.kind === CONTAINER_HINT_KIND.DYNAMIC_FORK
               && Array.isArray(hint.resolvedBranches)) {
      hintBranchCount = hint.resolvedBranches.filter(
        (b) => b && Array.isArray(b.childIds) && b.childIds.length > 0
      ).length;
    }
  }
  const effectiveLegCount = Math.max(branchLegs.length, hintBranchCount);

  if (!isDynamic) {
    // fork/degenerate-leg-count for static fork
    if (effectiveLegCount < 2) {
      push({
        nodeId: node.id,
        severity: effectiveLegCount === 0 ? SEVERITY.ERROR : SEVERITY.WARN,
        rule: 'fork/degenerate-leg-count',
        message: effectiveLegCount === 0
          ? 'Fork has no branches — degenerate. Remove it or add legs.'
          : 'Fork has only one branch. A single-leg fork serializes anyway; consider removing it.',
      });
    }
  } else {
    // dynfork/missing-list-expression
    // G426 — also accept the hint's branchExpr so dynamic forks that
    // carry their fan-out expression on the hint (and leave config bare)
    // don't get a spurious warning.
    const hintExpr = (hint && hint.kind === CONTAINER_HINT_KIND.DYNAMIC_FORK)
      ? hint.branchExpr
      : null;
    const expr = cfg.listExpression || cfg.dynamicTasksInput
      || cfg.dynamicTaskNameParam || hintExpr;
    if (!expr || String(expr).trim() === '') {
      push({
        nodeId: node.id,
        severity: SEVERITY.WARN,
        rule: 'dynfork/missing-list-expression',
        message:
          'Dynamic fork has no listExpression configured. Customers reading this canvas '
          + 'cannot tell what drives the fan-out. Add a listExpression (e.g., "$.items") '
          + 'so the intent is visible at def time.',
      });
    }
  }
}

function lintRawLoop(node, cfg, outs, push) {
  // loop/empty-condition
  //
  // RC-116a — Schema-aware iterator source check. CANVAS_TYPE.LOOP
  // covers TWO authored node types that both land here:
  //   * `loop` (single-pass, schema declares `collection`)
  //   * `do_while` (aliasOf: loop, schema declares `loopCondition`)
  // Pre-RC-116a the lint ONLY read `cfg.loopCondition`, so a node
  // authored against the `loop` schema (with `collection`) tripped
  // `loop/empty-condition` ERR even though every required field was
  // populated. Same class of drift as RC-114 / RC-112d.
  //
  // Fix: accept either canonical field. Both are intentional schema
  // shapes for the same IR node — neither is "wrong". The empty-condition
  // ERR now fires only when BOTH are blank.
  const cond = typeof cfg.loopCondition === 'string' ? cfg.loopCondition.trim() : '';
  const coll = typeof cfg.collection === 'string' ? cfg.collection.trim() : '';
  if (!cond && !coll) {
    push({
      nodeId: node.id,
      severity: SEVERITY.ERROR,
      rule: 'loop/empty-condition',
      message: 'Loop has no iterator source — set a collection (for-each) or condition (do-while).',
    });
  }

  // loop/empty-body — a loop with no default outgoing edge has nothing
  // inside it. The 'exit' edge doesn't count (that's the fall-through).
  const hasDefaultOut = outs.some((c) => (c.sourceHandle || 'default') === 'default');
  if (!hasDefaultOut) {
    push({
      nodeId: node.id,
      severity: SEVERITY.WARN,
      rule: 'loop/empty-body',
      message: 'DoWhile has an empty body — it will spin without doing work.',
    });
  }
}

function lintRawSubWorkflow(node, cfg, push) {
  // subwf/missing-refname
  //
  // G426 — Align with the IR builder's resolution chain so we don't
  // flag SubWorkflow nodes that the IR happily resolved. Order matches
  // flatToIR / CanvasJsonMaterializer:
  //   1. cfg.subWorkflowName   (canonical wire field)
  //   2. cfg.name              (legacy alias)
  //   3. _containerChildren.referencedWorkflow  (R3 typed hint)
  //   4. node.label            (last-resort fallback used by the IR)
  //
  // Pre-G426 the lint read only #1 and #2 and emitted ERR on any
  // SubWorkflow that carried its target on the hint or relied on the
  // label fallback — which was every materialized SubWorkflow node
  // produced after R3 shipped. The badge stuck even after the user
  // bound a target.
  const hint = getContainerHint(node);
  const hintRef = (hint && hint.kind === CONTAINER_HINT_KIND.SUB_WORKFLOW)
    ? hint.referencedWorkflow
    : null;
  // RC-116a — Schema-aware refname check. The `sub_workflow` schema
  // declares `workflowId` as the canonical field (a UUID picked from
  // a select-of-workflows). The Conductor importer historically wrote
  // `subWorkflowName`; the legacy alias path wrote `name`; IR
  // resolution wrote `referencedWorkflow` on the container hint;
  // node.label was the ultimate last-resort fallback. Pre-RC-116a
  // the lint omitted `workflowId` entirely, so a sub_workflow node
  // authored via the canonical schema (e.g. by AI build) tripped
  // `subwf/missing-refname` ERR even when the workflow was correctly
  // selected. Fix: read `workflowId` first, then the legacy chain.
  const candidate = cfg.workflowId || cfg.subWorkflowName || cfg.name || hintRef || node.label;
  const name = typeof candidate === 'string' ? candidate.trim() : '';
  if (!name) {
    push({
      nodeId: node.id,
      severity: SEVERITY.ERROR,
      rule: 'subwf/missing-refname',
      message: 'SubWorkflow has no refName — the engine has nothing to invoke.',
    });
  }
}

function lintRawTerminate(node, cfg, push) {
  // terminate/missing-reason — only fires for nodes explicitly typed as
  // 'terminate' (the raw-canvas pass already filtered for CANVAS_TYPE.TERMINATE).
  // End sentinels are `type: 'end'` and don't land here.
  //
  // RC-116a — Schema-aware reason check. The `terminate` schema declares
  // `terminationReason` (key-style, not field-style). Pre-RC-116a the
  // lint read `cfg.reason` only — so every node authored via the
  // canonical schema fired the INFO badge despite having a populated
  // reason. Accept either field; canonical first, legacy fallback.
  const reason = (typeof cfg.terminationReason === 'string' && cfg.terminationReason.trim())
    || (typeof cfg.reason === 'string' && cfg.reason.trim())
    || '';
  if (!reason) {
    push({
      nodeId: node.id,
      severity: SEVERITY.INFO,
      rule: 'terminate/missing-reason',
      message:
        'Terminate has no reason text. Add one so the execution trace logs why '
        + 'the workflow ended here — this is what your on-call will want at 2am.',
    });
  }
}

// ───────────────────────────────────────────────────────────────────
//  Pass 2: IR-graph rules
// ───────────────────────────────────────────────────────────────────

function runIrRules(ctx) {
  // Walk the IR and run rules that need IR context (e.g., join semantics).
  walkIr(ctx.ir.sequence || [], (n) => {
    try {
      if (n.kind === IR_KIND.FORK) ruleIrForkJoinAnyWithSingleLeg(n, ctx);
    } catch { /* swallow */ }
  });

  // Unreachable-node detection.
  try {
    ruleIrUnreachableNodes(ctx);
  } catch { /* swallow */ }
}

function ruleIrForkJoinAnyWithSingleLeg(n, ctx) {
  const join = n.join || {};
  if (join.waitMode === 'ANY' && (n.branches || []).length <= 1) {
    ctx.push({
      nodeId: n.id,
      severity: SEVERITY.WARN,
      rule: 'fork/join-any-single-leg',
      message:
        'Join waitMode is ANY but the fork has 1 or 0 branches. ANY-completion over a '
        + 'single leg is equivalent to a plain sequence — remove the fork or add legs.',
    });
  }
}

function ruleIrUnreachableNodes(ctx) {
  // Collect every flat-canvas node id that flatToIR touched (directly in
  // the IR or wired as a container's join). Anything else is unreachable.
  const seen = new Set();
  collectAllIrNodeIds(ctx.ir.sequence || [], seen);
  if (ctx.ir.start) seen.add(ctx.ir.start.id);
  if (ctx.ir.end) seen.add(ctx.ir.end.id);

  /* G267 — A cognitive agent's TOOL CATALOG is reachable by design.
   *
   * flatToIR intentionally keeps cognitive fan-out skills off the structural trunk — the LLM
   * decides which to invoke at runtime, so they are not a sequence (AGENT_EXECUTION_MODEL §4).
   * This rule reuses the IR walk to answer "is it reachable?", which is a DIFFERENT question,
   * and therefore flagged the agent's own tools as `graph/unreachable-node` WARNs. A tool that
   * is the target of a `cognitive_avail` edge is reachable via the loop; never warn on it. */
  const cognitiveToolIds = new Set();
  for (const c of (ctx.payload.connections || [])) {
    const handle = c && (c.sourceHandle ?? c.fromPort);
    const target = c && (c.target ?? c.to);
    if (handle === 'cognitive_avail' && target) cognitiveToolIds.add(target);
  }

  for (const src of ctx.payload.nodes) {
    if (!src || !src.id) continue;
    if (seen.has(src.id)) continue;
    if (cognitiveToolIds.has(src.id)) continue;
    const t = normalizeCanvasType(src.type);
    // Start-like, end-like, and fork-join nodes are absorbed / sentinel —
    // their ids may not appear in the IR walk but that doesn't mean the
    // customer orphaned them.
    if (t === CANVAS_TYPE.START || t === CANVAS_TYPE.END
        || t === CANVAS_TYPE.PARALLEL_JOIN || t === CANVAS_TYPE.CONVERGENCE) {
      continue;
    }
    ctx.push({
      nodeId: src.id,
      severity: SEVERITY.WARN,
      rule: 'graph/unreachable-node',
      message:
        'Node is declared but not reachable from Start. Connect it to a predecessor '
        + 'or delete it — unreachable nodes never execute.',
    });
  }
}

// ───────────────────────────────────────────────────────────────────
//  IR walker — recurses through every container's children.
// ───────────────────────────────────────────────────────────────────

function walkIr(nodes, visit) {
  for (const n of nodes || []) {
    visit(n);
    if (n.kind === IR_KIND.SWITCH) {
      for (const c of n.cases || []) walkIr(c.children, visit);
      if (n.defaultCase) walkIr(n.defaultCase.children, visit);
    } else if (n.kind === IR_KIND.FORK) {
      for (const b of n.branches || []) walkIr(b.children, visit);
    } else if (n.kind === IR_KIND.DYNAMIC_FORK) {
      if (Array.isArray(n.resolvedBranches)) {
        for (const b of n.resolvedBranches) walkIr(b.children, visit);
      }
    } else if (n.kind === IR_KIND.DO_WHILE) {
      walkIr(n.body, visit);
    } else if (n.kind === IR_KIND.SUB_WORKFLOW) {
      if (n.embedded && n.embedded.kind) walkIr([n.embedded], visit);
    }
  }
}

function collectAllIrNodeIds(nodes, out) {
  for (const n of nodes || []) {
    if (n && n.id) out.add(n.id);
    if (n.kind === IR_KIND.SWITCH) {
      for (const c of n.cases || []) collectAllIrNodeIds(c.children, out);
      if (n.defaultCase) collectAllIrNodeIds(n.defaultCase.children, out);
    } else if (n.kind === IR_KIND.FORK) {
      for (const b of n.branches || []) collectAllIrNodeIds(b.children, out);
      if (n.join && n.join.id) out.add(n.join.id);
    } else if (n.kind === IR_KIND.DYNAMIC_FORK) {
      if (Array.isArray(n.resolvedBranches)) {
        for (const b of n.resolvedBranches) collectAllIrNodeIds(b.children, out);
      }
      if (n.join && n.join.id) out.add(n.join.id);
    } else if (n.kind === IR_KIND.DO_WHILE) {
      collectAllIrNodeIds(n.body, out);
    } else if (n.kind === IR_KIND.SUB_WORKFLOW) {
      if (n.embedded && n.embedded.kind) collectAllIrNodeIds([n.embedded], out);
    }
  }
}

// ───────────────────────────────────────────────────────────────────
//  Helpers
// ───────────────────────────────────────────────────────────────────

function indexOutgoing(connections) {
  // Normalize each connection so callers can read `sourceHandle`
  // regardless of whether the canvas state stores it as the React-Flow
  // `sourceHandle` field or the flat-canvas `fromPort` field. Without
  // this normalization, every Conductor-converted fork (which the
  // backend emits as fromPort: "branch_0".."branch_5") matched zero
  // branch_* handles in lintRawFork → false-positive
  // 'fork/degenerate-leg-count' ERR badge on every fork in the canvas.
  // useCanvas.loadCanvasData stores connections in flat-canvas shape,
  // so the lint must accept that shape too.
  const map = new Map();
  for (const c of connections || []) {
    if (!c) continue;
    const src = c.source != null ? c.source : c.from;
    if (!src) continue;
    const sourceHandle = c.sourceHandle != null
        ? c.sourceHandle
        : (c.fromPort != null ? c.fromPort : null);
    const targetHandle = c.targetHandle != null
        ? c.targetHandle
        : (c.toPort != null ? c.toPort : null);
    const target = c.target != null ? c.target : c.to;
    const norm = { ...c, source: src, target, sourceHandle, targetHandle };
    const list = map.get(src);
    if (list) list.push(norm);
    else map.set(src, [norm]);
  }
  return map;
}

/**
 * Normalize connection shape so flatToIR accepts it.
 * Same contract as flatToElkHierarchical::normalizeConnection.
 */
function normalize(payload) {
  return {
    nodes: payload.nodes,
    connections: payload.connections.map((c) => {
      const source = c && c.source != null ? c.source : (c && c.from);
      const target = c && c.target != null ? c.target : (c && c.to);
      const sourceHandle = c && c.sourceHandle != null
        ? c.sourceHandle
        : (c && c.fromPort != null ? c.fromPort : 'default');
      return { source, target, sourceHandle };
    }),
  };
}

// ============================================================================
// RC-116a — V2LINT DRIFT VALIDATOR
// ============================================================================
//
// Same pattern as v3Validate.js's RC-112b validator: a module-init pass that
// asserts every config-field name this lint relies on is actually declared
// in the corresponding NODE_TYPES[type].configSchema. Throws in dev/test,
// console.errors in prod so regressions fail loudly during build but never
// break the running canvas.
//
// WHY THIS EXISTS
// ───────────────
// Pre-RC-116a v2Lint had three silent drifts:
//   * lintRawLoop read cfg.loopCondition but the 'loop' schema declares
//     cfg.collection. Operators using the canonical schema tripped
//     `loop/empty-condition` ERR.
//   * lintRawSubWorkflow read cfg.subWorkflowName | cfg.name but the
//     'sub_workflow' schema declares cfg.workflowId. AI-generated and
//     schema-authored sub_workflow nodes both tripped
//     `subwf/missing-refname` ERR.
//   * lintRawTerminate read cfg.reason but the 'terminate' schema
//     declares cfg.terminationReason. Every schema-authored terminate
//     fired the INFO badge despite having a populated reason.
//
// All three were the same RC-114-class drift the schema-aware fork-branch
// check fixed once: lint hardcoded one field name, schema declared another,
// no validator caught the divergence. RC-116a fixes the three drifts
// AND installs this validator so the next one trips at module init
// instead of in production.
//
// TO ADD A NEW LINT FIELD REFERENCE
// ─────────────────────────────────
//   1. Add the field read to the lint function.
//   2. Add the (type, [field]) pair to LINT_FIELD_REFERENCES below.
//   3. If the field is a legacy fallback (not in the schema by design),
//      DO NOT add it to LINT_FIELD_REFERENCES — only canonical fields
//      need to track the schema. Document the fallback in a comment.
//
// PIPE-SYNTAX
// ───────────
//   'a|b' means "either field is acceptable" — matches RC-112b's
//   convention for the `terminate` row in v3Validate.

const LINT_FIELD_REFERENCES = Object.freeze({
  // ── CANVAS_TYPE.CONDITION (lintRawSwitch) ──────────────────────
  // 'condition' schema: expression (only).
  // 'switch_case' schema: expression + cases.
  // The lint accepts legacy fallbacks (switchExpression, conditionExpression,
  // condition, action) but only `expression` / `cases` need schema tracking.
  condition:    ['expression'],
  switch_case:  ['expression', 'cases'],

  // ── CANVAS_TYPE.PARALLEL_FORK (lintRawFork) ────────────────────
  // Schema field: branches (verified RC-114 / RC-112d). The dynamic-fork
  // variant's listExpression is a runtime / hint variant, not on the
  // canonical static schema; we don't track it.
  parallel_fork: ['branches'],

  // ── CANVAS_TYPE.LOOP (lintRawLoop) ─────────────────────────────
  // Two authored types funnel into this lint:
  //   * 'loop' schema:     collection (single-pass for-each)
  //   * 'do_while' schema: loopCondition (iteration with exit predicate;
  //                        aliasOf: loop — uses key: not field:)
  // RC-116a fix made the lint accept EITHER. The drift validator
  // asserts the corresponding canonical per type.
  loop:        ['collection'],
  do_while:    ['loopCondition'],

  // ── CANVAS_TYPE.SUB_WORKFLOW (lintRawSubWorkflow) ──────────────
  // Schema field: workflowId (canonical, a UUID select).
  // Legacy: subWorkflowName | name | hintRef | label — these are
  // intentionally accepted by the lint for back-compat with imported
  // / hint-resolved canvases but aren't on the canonical schema and
  // therefore not tracked here.
  sub_workflow: ['workflowId'],

  // ── CANVAS_TYPE.TERMINATE (lintRawTerminate) ───────────────────
  // Schema uses key: 'terminationReason' (key-style, not field-style;
  // the validator's accessor handles both). RC-116a fix accepts
  // terminationReason OR the legacy 'reason' field.
  terminate:    ['terminationReason'],
});

(function validateLintFieldReferencesAgainstSchemas() {
  const errors = [];
  for (const [type, fields] of Object.entries(LINT_FIELD_REFERENCES)) {
    const def = NODE_TYPES[type];
    if (!def) {
      errors.push(`LINT_FIELD_REFERENCES['${type}']: no NODE_TYPES entry`);
      continue;
    }
    // Honor alias chains so a node whose schema is `aliasOf: 'X'` inherits
    // X's field declarations. This matches v3Validate's behavior so
    // schema fixes in the canonical entry propagate to its aliases for
    // free.
    const collectFields = (entry) => new Set(
      (entry.configSchema || [])
        .map((f) => f && (f.field || f.key))
        .filter(Boolean),
    );
    let declared = collectFields(def);
    if (declared.size === 0 && def.aliasOf && NODE_TYPES[def.aliasOf]) {
      declared = collectFields(NODE_TYPES[def.aliasOf]);
    }
    for (const fieldSpec of fields) {
      const alts = String(fieldSpec).split('|');
      const anyAlt = alts.some((alt) => declared.has(alt));
      if (!anyAlt) {
        errors.push(
          `LINT_FIELD_REFERENCES['${type}']: declared lint-required field `
          + `"${fieldSpec}" is not in NODE_TYPES['${type}'].configSchema `
          + `[${[...declared].join(', ') || '(empty)'}]`,
        );
      }
    }
  }
  if (errors.length === 0) return;
  const msg = `[v2Lint/RC-116a] LINT_FIELD_REFERENCES ↔ NODE_TYPES drift detected:\n  - ${errors.join('\n  - ')}\n`
    + `Fix in v2Lint.js: update the lint function to read the schema's actual `
    + `field name (and add a fallback for the legacy name if old canvases `
    + `still need to work), OR rename the field in soarNodes.js to match.`;
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
    throw new Error(msg);
  } else if (typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.error(msg);
  }
})();
